import React, { useState } from 'react';
import { X, AlertTriangle, Flag, FileWarning, Shield, Skull, Ban, Copyright, HelpCircle, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const REPORT_REASONS = [
  { id: 'spam', label: 'Spam or misleading', icon: Ban, color: 'text-gray-500' },
  { id: 'harassment', label: 'Harassment or bullying', icon: AlertTriangle, color: 'text-orange-500' },
  { id: 'hate_speech', label: 'Hate speech or symbols', icon: Skull, color: 'text-red-600' },
  { id: 'violence', label: 'Violence or dangerous organizations', icon: Shield, color: 'text-red-500' },
  { id: 'misinformation', label: 'False or inaccurate information', icon: FileWarning, color: 'text-amber-500' },
  { id: 'adult_content', label: 'Adult or sensitive content', icon: Ban, color: 'text-pink-500' },
  { id: 'copyright', label: 'Copyright or trademark violation', icon: Copyright, color: 'text-blue-500' },
  { id: 'other', label: 'Other', icon: HelpCircle, color: 'text-gray-400' },
];

const ReportModal = ({ isOpen, onClose, postId, postAuthor }) => {
  const [selectedReason, setSelectedReason] = useState(null);
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1); // 1 = select reason, 2 = add details

  const handleSubmit = async () => {
    if (!selectedReason) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/feed/report`, {
        post_id: postId,
        reason: selectedReason,
        details: details.trim() || null
      }, { withCredentials: true });
      
      toast.success('Thank you for reporting. We\'ll review this post.');
      onClose();
      setSelectedReason(null);
      setDetails('');
      setStep(1);
    } catch (error) {
      if (error.response?.data?.detail?.includes('already reported')) {
        toast.info('You have already reported this post');
        onClose();
      } else {
        toast.error('Failed to submit report');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleReasonSelect = (reasonId) => {
    setSelectedReason(reasonId);
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
    setDetails('');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="report-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-red-500" />
              <h3 className="font-bold text-lg">Report Post</h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              data-testid="close-report-modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            {step === 1 ? (
              <>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Why are you reporting this post by @{postAuthor}?
                </p>
                <div className="space-y-2">
                  {REPORT_REASONS.map((reason) => (
                    <button
                      key={reason.id}
                      onClick={() => handleReasonSelect(reason.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all hover:bg-gray-50 dark:hover:bg-gray-700 ${
                        selectedReason === reason.id
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 dark:border-gray-700'
                      }`}
                      data-testid={`report-reason-${reason.id}`}
                    >
                      <reason.icon className={`w-5 h-5 ${reason.color}`} />
                      <span className="font-medium text-left">{reason.label}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-4">
                  <button
                    onClick={handleBack}
                    className="text-sm text-primary hover:underline"
                  >
                    ← Back
                  </button>
                  <span className="text-sm text-gray-500">
                    Reporting for: {REPORT_REASONS.find(r => r.id === selectedReason)?.label}
                  </span>
                </div>
                
                {selectedReason === 'misinformation' && (
                  <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                    <div className="flex items-start gap-2">
                      <FileWarning className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                          Help us understand what's inaccurate
                        </p>
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                          After reporting, you can also add a Community Note with fact-checking information.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <Textarea
                  placeholder="Add more details (optional)..."
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  className="rounded-xl resize-none mb-4"
                  rows={4}
                  data-testid="report-details"
                />
                
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full rounded-full bg-red-500 hover:bg-red-600 text-white"
                  data-testid="submit-report"
                >
                  {submitting ? (
                    'Submitting...'
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Report
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ReportModal;
